<template lang="html">
  <v-content>
    <TodoDetailDialog
      :opened.sync="todoDetailDialogOpened"
      :task="currentTask"
    />

    <AddTodoDialog :opened.sync="addTodoDialogOpened" />

    <TodoAutomationDialog :opened.sync="automationDialogOpened" />

    <v-container style="max-width: 1400px">
      <v-layout class="mt-2 mb-3" align-center>
        <soraban-action-button
          @click="$router.go(-1)"
          icon="arrow_back"
          help="Back"
        />

        <h1>To-do Tasks</h1>
        <v-spacer />

        <v-btn
          color="primary"
          class="text-capitalize"
          @click="addTodoDialogOpened = true"
        >
          <v-icon small class="pr-2">add</v-icon>
          Add To-do
        </v-btn>

        <v-btn
          color="primary"
          class="text-capitalize"
          @click="automationDialogOpened = true"
        >
          <v-icon small class="pr-2">manage_history</v-icon>
          Automation
        </v-btn>

        <soraban-action-button
          @click="refreshToDoList()"
          :disabled="refreshing"
          icon="refresh"
          class="success--text"
        />
      </v-layout>

      <v-card class="content-container px-3">
        <v-layout align-center wrap class="mt-2 mb-4">
          <v-tabs v-model="activeTab" color="transparent">
            <v-tab class="text-capitalize"> Active tasks </v-tab>
            <v-tab class="text-capitalize"> Suggested tasks </v-tab>
            <v-tab class="text-capitalize"> Completed tasks </v-tab>
          </v-tabs>
        </v-layout>

        <v-layout>
          <v-flex xs12 sm3 class="pr-2">
            <div class="text-xs-left">Search</div>
            <v-text-field
              v-model="searchTerm"
              class="default-input subheading"
              solo
              hide-details
              placeholder="Search"
            />
          </v-flex>
          <v-flex xs2 class="px-2">
            <div class="text-xs-left">Priority</div>
            <v-select
              v-model="priorityFilter"
              :items="allPriorities"
              hide-details
              solo
              class="default-input"
              @change="refreshTodoList()"
            />
          </v-flex>
          <v-flex xs2 class="px-2">
            <div class="text-xs-left">Entity</div>
            <v-select
              v-model="entityFilter"
              :items="allEntities"
              hide-details
              solo
              class="default-input"
              @change="refreshTodoList()"
            />
          </v-flex>
          <v-flex xs2 class="px-2">
            <div class="text-xs-left">Tags</div>
            <v-select
              v-model="tagFilters"
              :items="allTags"
              hide-details
              solo
              class="default-input"
              placeholder="All"
              multiple
            />
          </v-flex>
        </v-layout>

        <v-data-table
          :headers="headers"
          :items="filteredItems"
          :loading="tableLoading"
          :select-all="$vuetify.breakpoint.mdAndUp"
          class="clients-table text-xs-left mt-3"
          disable-initial-sort
        >
          <template v-slot:items="props">
            <tr class="task-row" :key="props.index">
              <td class="hidden-sm-and-down">
                <v-checkbox
                  v-model="props.selected"
                  primary
                  color="lightgrey"
                  hide-details
                ></v-checkbox>
              </td>

              <td class="text-xs-left">
                <div
                  class="task-title"
                  v-html="props.item.title"
                  @click="viewTask(props.item)"
                />
              </td>

              <td class="text-xs-left">
                <v-chip small class="priority" :class="props.item.priority">
                  {{ props.item.priority }}
                </v-chip>
              </td>

              <td class="text-xs-left">
                <v-layout>
                  <v-chip
                    v-for="tag in props.item.tags"
                    small
                    class="tag"
                    :key="tag"
                  >
                    {{ tag }}
                  </v-chip>
                </v-layout>
              </td>

              <td class="text-xs-left">
                <span>{{ moment(props.item.createdAt).fromNow() }}</span>
              </td>

              <td class="text-xs-left">
                <span>{{
                  props.item.completedAt !== null
                    ? moment(props.item.completedAt).fromNow()
                    : 'N/A'
                }}</span>
              </td>

              <td class="text-xs-left">
                <v-btn
                  v-for="action in props.item.actions"
                  color="primary"
                  :key="action"
                  class="text-none ml-0"
                  small
                >
                  {{ action }}
                </v-btn>

                <v-btn
                  v-if="activeTab === 2"
                  color="primary"
                  outline
                  class="text-none ml-0"
                  small
                  @click="restore(props.item)"
                >
                  Restore
                </v-btn>

                <v-btn
                  v-if="activeTab !== 2"
                  color="primary"
                  outline
                  class="text-none ml-0"
                  small
                  @click="markAsCompleted(props.item)"
                >
                  Mark as completed
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </v-container>
  </v-content>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import API from '@/api';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import AddTodoDialog from '@/components/dialogs/AddTodoDialog';
import TodoAutomationDialog from '@/components/dialogs/TodoAutomationDialog';
import TodoDetailDialog from '@/components/dialogs/TodoDetailDialog';
import moment from 'moment';

export default {
  name: 'TodoTasks',

  components: {
    AddTodoDialog,
    TodoDetailDialog,
    TodoAutomationDialog,
  },

  data() {
    return {
      refreshing: false,
      tableLoading: false,

      todoDetailDialogOpened: false,
      addTodoDialogOpened: false,
      automationDialogOpened: false,

      activeTab: 0,
      searchTerm: '',
      entityFilter: 'All',
      tagFilters: [],
      priorityFilter: 'All',
      allPriorities: ['All', 'Urgent', 'High', 'Medium', 'Low'],

      headers: [
        {
          text: 'Task',
          align: 'left',
          value: 'title',
        },
        {
          text: 'Priority',
          align: 'left',
          value: 'text',
        },
        {
          text: 'Tags',
          align: 'left',
          value: 'tags',
        },
        {
          text: 'Created at',
          align: 'left',
          value: 'createdAt',
        },
        {
          text: 'Completed at',
          align: 'left',
          value: 'completedAt',
        },
        {
          text: 'Actions',
          align: 'left',
        },
      ],

      todoItems: [
        {
          id: 0,
          title:
            'John Doe completed the questionnaire and paid the remaining payment.',
          description:
            "<a>John Doe</a> completed the questionnaire and paid the remaining payment. Please update the status to 'paid' in PracticeCS.",
          priority: 'Urgent',
          completedAt: null,
          createdAt: moment().subtract(1, 'min'),
          entities: ['John Doe'],
          tags: ['PracticeCS', 'Test'],
        },
        {
          id: 3,
          title: 'Jane Doe uploaded the 7 documents to Soraban Documents',
          description:
            '<a>Jane Doe</a> uploaded the 7 documents to Soraban Documents. Please move them into Sharefiles.',
          priority: 'High',
          completedAt: moment().subtract(1, 'minutes'),
          createdAt: moment().subtract(1, 'hours'),
          entities: ['Jane Doe'],
          tags: ['Sharefiles'],
        },
        {
          id: 2,
          title:
            'Frank Johnson approved the fee that was in the Engagement letter by making the payment',
          description:
            "<a>Frank Johnson</a> approved the fee that was in the Engagement letter by making the payment. Please update the status to 'paid' in PracticeCS.",
          priority: 'Low',
          completedAt: null,
          createdAt: moment().subtract(2, 'hours'),
          entities: ['Frank Johnson'],
          tags: ['PracticeCS'],
        },
        {
          id: 1,
          title:
            'Jane Doe signed the Engagement letter. Please update the status in PracticeCS.',
          description:
            '<a>Jane Doe</a> signed the Engagement letter. Please update the status in PracticeCS.',
          priority: 'Medium',
          completedAt: moment().subtract(1, 'hours'),
          createdAt: moment().subtract(1, 'days'),
          entities: ['Jane Doe'],
          tags: ['PracticeCS'],
        },
        {
          id: 4,
          title:
            'John Doe changed the name of 4 of the documents. Please update the file name in Sharefiles.',
          description:
            '<a>John Doe</a> changed the name of 4 of the documents. Please update the file name in Sharefiles.',
          priority: 'Urgent',
          completedAt: moment().subtract(1, 'day'),
          createdAt: moment().subtract(2, 'days'),
          entities: ['John Doe'],
          tags: ['Sharefiles'],
        },
        {
          id: 5,
          title: 'Remind clients to begin questionnaires.',
          priority: 'High',
          completedAt: null,
          suggested: true,
          createdAt: moment().subtract(2, 'days'),
          entities: [],
          tags: [],
          actions: ['Review Questionnaires'],
        },
        {
          id: 6,
          title: 'Remind clients to continue questionnaires.',
          priority: 'High',
          completedAt: null,
          suggested: true,
          createdAt: moment().subtract(2, 'days'),
          entities: [],
          tags: [],
          actions: ['Review Questionnaires'],
        },
        {
          id: 7,
          title: 'Remind clients to answer staff messages.',
          priority: 'Low',
          completedAt: null,
          suggested: true,
          createdAt: moment().subtract(2, 'days'),
          entities: [],
          tags: [],
          actions: ['Review Conversations'],
        },
        {
          id: 8,
          title: 'Answer client messages.',
          priority: 'Low',
          completedAt: null,
          suggested: true,
          createdAt: moment().subtract(2, 'days'),
          entities: [],
          tags: [],
          actions: ['Review Conversations'],
        },
      ],

      currentTask: null,
    };
  },

  computed: {
    allEntities() {
      const allEntities = this.todoItems.flatMap(item => {
        return item.entities;
      });

      return uniq(['All', ...allEntities]);
    },

    allTags() {
      const allTags = this.todoItems.flatMap(item => {
        return item.tags;
      });

      return uniq(allTags);
    },

    filteredItems() {
      const searchTerm = this.searchTerm.toLowerCase();

      return this.todoItems.filter(row => {
        let rowExists = true;

        // Suggested tasks
        if (this.activeTab === 1) {
          rowExists = row.suggested === true && row.completedAt === null;

          // completed tasks
        } else if (this.activeTab === 2) {
          rowExists = row.completedAt !== null;

          // Active tasks
        } else {
          rowExists = row.completedAt === null && !row.suggested;
        }

        if (rowExists) {
          if (searchTerm.length) {
            rowExists = row.text.toLowerCase().includes(searchTerm);
          }

          if (this.entityFilter !== 'All') {
            rowExists = row.entities.includes(this.entityFilter);
          }

          if (this.tagFilters.length > 0) {
            rowExists =
              typeof row.tags.find(tag => this.tagFilters.includes(tag)) !==
              'undefined';
          }

          if (this.priorityFilter !== 'All') {
            rowExists = row.priority === this.priorityFilter;
          }
        }

        return rowExists;
      });
    },
  },

  methods: {
    moment,
    isEmpty,

    viewTask(task) {
      this.todoDetailDialogOpened = true;
      this.currentTask = task;
    },

    refreshTodoList() {},

    markAsCompleted(task) {
      task.completedAt = moment().toDate();
    },

    restore(task) {
      task.completedAt = null;
    },
  },

  created() {},
};
</script>

<style lang="postcss" scoped>
.task-row > td {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.task-title {
  cursor: pointer;
}

.tag {
  margin-left: 0;
  margin-right: 6px;
  flex: 0 !important;
}

.priority {
  margin-left: 0;
  margin-right: 6px;
  flex: 0 !important;

  &.Urgent {
    color: white;
    background-color: red;
  }
}
</style>
